import { Box, HStack, KdsLink, KdsText } from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { type CatalogMediumOptionReadDto } from "@/config/apiClient";
import { useExpertMode } from "@/hooks";
import { useRolePermission } from "@/hooks/rolePermission/useRolePermission";
import { TechnicalSpecificationsCategoryList } from "./_partials/TechnicalSpecificationsCategoryList";
import { type TechnicalSpecificationsProps } from "./TechnicalSpecifications.types";

export const TechnicalSpecifications = ({
  orderItem,
  technicalOptions,
  handleQualifyDrawerOpen,
}: TechnicalSpecificationsProps) => {
  const { t } = useTranslate();
  const { hasBrokerKazaarRole } = useRolePermission();

  const catalogMediumAdmaId = orderItem.sandboxItem.catalogMediumAdmaId;
  const catalogMediumDeliverableId =
    orderItem.sandboxItem.catalogMediumDeliverableId;

  const { useGetTechnicalFields } = useExpertMode();
  const { data: attributesData } = useGetTechnicalFields({
    admaId: catalogMediumAdmaId || 0,
    awsVersion: orderItem.sandboxItem.catalogMedium?.technicalVersion || 1,
  });

  const productName = attributesData?.data?.name;
  const deliverableName = attributesData?.data?.deliverables?.find(
    (deliverable) => deliverable.deliverable_id === catalogMediumDeliverableId
  )?.name;

  const groupedTechnicalOptions = technicalOptions.reduce<
    Record<string, typeof technicalOptions>
  >((acc, option) => {
    if (!option.category) {
      return acc;
    }
    if (!acc[option.category]) {
      acc[option.category] = [];
    }

    acc[option.category].push(option);
    return acc;
  }, {});

  const productDeliverableOptions: CatalogMediumOptionReadDto[] = [
    ...(productName
      ? [
          {
            id: "productName",
            name: t("openBrief.qualifyDrawer.fields.productName"),
            value: t(productName),
          } as CatalogMediumOptionReadDto,
        ]
      : []),
    ...(deliverableName
      ? [
          {
            id: "deliverableName",
            name: t("openBrief.qualifyDrawer.fields.deliverableName"),
            value: t(deliverableName),
          } as CatalogMediumOptionReadDto,
        ]
      : []),
  ];

  const optionsByCategory = (() => {
    const entries = Object.entries(groupedTechnicalOptions)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(
        ([category, options]) =>
          [category, options] as [string, CatalogMediumOptionReadDto[]]
      );

    if (productDeliverableOptions.length > 0) {
      if (entries.length > 0) {
        entries[0][1] = [...productDeliverableOptions, ...entries[0][1]];
      } else {
        entries.push(["1", productDeliverableOptions]);
      }
    }

    return entries;
  })();

  return (
    <Box p="sp-md" bgColor="elevation.second" rounded="br-sm" w="100%">
      {!technicalOptions.length && !productName && !deliverableName && (
        <HStack gap="sp-sm" align="center" justify="center">
          <KdsText size="subtitle-sm" color="text-bodyvariant">
            {t("expertMode.technicalSpecifications.noOptions")}
          </KdsText>

          {hasBrokerKazaarRole && !technicalOptions.length && (
            <>
              <KdsText size="subtitle-sm" color="text-bodyvariant">
                -
              </KdsText>

              <KdsLink
                href="#"
                size="sm"
                state="neutral"
                onPress={handleQualifyDrawerOpen}
              >
                {t("expertMode.technicalSpecifications.qualifyProduct")}
              </KdsLink>
            </>
          )}
        </HStack>
      )}

      {(technicalOptions.length > 0 || productName || deliverableName) && (
        <TechnicalSpecificationsCategoryList
          optionsByCategory={optionsByCategory}
        />
      )}
    </Box>
  );
};
